import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';
import PendingApprovalsContext from './PendingApprovalsContext';
import * as rescheduleApi from '../pages/students/rescheduleApi';
import wsManager from '../services/wsManager';
import { playNotifySound } from '../utils/notifySound';

const FLASH_TITLE = '🔔 Нове повідомлення';
const FLASH_INTERVAL_MS = 1000;

function studentName(student) {
   if (!student) return '';
   return (student.name || '') + ' ' + (student.surname || '');
}

function normalizeReschedule(r) {
   const lesson = r.lesson || {};
   return {
      type: 'reschedule',
      id: r.id,
      studentId: r.initiatedBy ? r.initiatedBy.id : null,
      studentName: studentName(r.initiatedBy),
      summary: studentName(r.initiatedBy) + ' пропонує перенести заняття ' + (lesson.date || '') + (r.currentProposedDate ? ' на ' + r.currentProposedDate : ''),
      dAdd: r.dAdd,
      raw: r,
   };
}
function normalizeCancel(c) {
   const lesson = (c.event && c.event.lesson) || {};
   return {
      type: 'cancel',
      id: c.id,
      studentId: c.student ? c.student.id : null,
      studentName: studentName(c.student),
      summary: studentName(c.student) + ' просить скасувати заняття ' + (lesson.date || '') + (lesson.start ? ', ' + lesson.start : ''),
      dAdd: c.dAdd,
      raw: c,
   };
}
function normalizeApproval(a) {
   return {
      type: 'approval',
      id: a.id,
      studentId: a.student ? a.student.id : null,
      studentName: studentName(a.student),
      // TeacherApprovalRequest.message is already a full human-readable
      // summary generated server-side (e.g. "Ivan wants to see the topic
      // element in 'Present Perfect'") - no need to build our own.
      summary: a.message,
      dAdd: a.requestedAt,
      raw: a,
   };
}

// Single source of truth for "everything waiting on the teacher across every
// student" - mounted once in App.js (logged-in branch only), alongside the
// existing AuthContext/DetailsContext providers this app already uses.
// Combines 3 independently-tracked backend features (reschedule, cancel,
// the older blur-reveal TeacherApprovalRequest) into one list, refetching on
// a single new global WS topic rather than trying to reconcile 3 different
// push payloads. On top of the data, this also owns the side effects the
// user asked for: a synthesized beep + a flashing browser-tab title
// whenever something GENUINELY NEW arrives (tracked by id-set diff, not a
// raw count, so "one resolved + one new in the same refetch" still counts).
export default function PendingApprovalsProvider({ children }) {
   const { isLoggedIn } = useContext(AuthContext);
   const [items, setItems] = useState([]);
   const [loading, setLoading] = useState(true);
   const knownKeysRef = useRef(null); // null until the first real fetch completes
   const originalTitleRef = useRef(document.title);
   const flashTimerRef = useRef(null);

   const stopFlashing = () => {
      if (flashTimerRef.current) {
         clearInterval(flashTimerRef.current);
         flashTimerRef.current = null;
      }
      document.title = originalTitleRef.current;
   };

   const startFlashing = () => {
      if (flashTimerRef.current) return;
      originalTitleRef.current = document.title;
      let showingAlert = false;
      flashTimerRef.current = setInterval(() => {
         showingAlert = !showingAlert;
         document.title = showingAlert ? FLASH_TITLE : originalTitleRef.current;
      }, FLASH_INTERVAL_MS);
   };

   useEffect(() => {
      const onVisibilityChange = () => {
         if (!document.hidden) stopFlashing();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', onVisibilityChange);
      return () => {
         document.removeEventListener('visibilitychange', onVisibilityChange);
         window.removeEventListener('focus', onVisibilityChange);
         stopFlashing();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const load = () => {
      Promise.all([
         rescheduleApi.getAllPendingRescheduleRequests().catch(() => ({ data: [] })),
         rescheduleApi.getAllPendingCancelRequests().catch(() => ({ data: [] })),
         axios.get('/api/teacher-approval-requests', { withCredentials: true }).catch(() => ({ data: [] })),
      ]).then(([rescheduleRes, cancelRes, approvalRes]) => {
         const reschedules = (Array.isArray(rescheduleRes.data) ? rescheduleRes.data : []).map(normalizeReschedule);
         const cancels = (Array.isArray(cancelRes.data) ? cancelRes.data : []).map(normalizeCancel);
         const approvals = (Array.isArray(approvalRes.data) ? approvalRes.data : []).map(normalizeApproval);
         const combined = [...reschedules, ...cancels, ...approvals];

         const newKeys = new Set(combined.map((item) => item.type + ':' + item.id));
         if (knownKeysRef.current) {
            const hasGenuinelyNewItem = [...newKeys].some((key) => !knownKeysRef.current.has(key));
            if (hasGenuinelyNewItem) {
               playNotifySound();
               if (document.hidden) startFlashing();
            }
         }
         knownKeysRef.current = newKeys;

         setItems(combined);
         setLoading(false);
      });
   };

   useEffect(() => {
      if (!isLoggedIn) { setItems([]); setLoading(true); knownKeysRef.current = null; return; }
      load();
      const handler = () => load();
      wsManager.subscribe('/topic/pending-approvals', handler);
      return () => wsManager.unsubscribe('/topic/pending-approvals', handler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isLoggedIn]);

   return (
      <PendingApprovalsContext.Provider value={{ items, loading, reload: load }}>
         {children}
      </PendingApprovalsContext.Provider>
   );
}
